import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import api from '../services/api'
import SortableTh, { sortRows } from '../components/SortableTh'
import Paginacao, { usePaginacao } from '../components/Paginacao'
import IconExcluir from '../components/icons/IconExcluir'
import { avisar, confirmar } from '../components/Avisos'
import SelecaoMultipla from '../components/SelecaoMultipla'

// DATE do Postgres vira JSON como "AAAA-MM-DDT00:00:00.000Z" (meia-noite UTC) — reconstruir com o
// "Z" faz o toLocaleDateString num fuso atras de UTC (Brasil) voltar 1 dia. Por isso sempre
// descarta a hora/fuso vindos do backend e ancora de novo em meia-noite LOCAL.
const fmtData = (d) => d ? new Date(d.split('T')[0] + 'T00:00:00').toLocaleDateString('pt-BR') : '—'
// Duas origens diferentes de valor passam por aqui: o decimal cru vindo do banco (parcelas_atraso,
// ex. "332.13" — ponto e' separador decimal) e a string ja formatada em apolices.parcelas (ex.
// "R$ 332,13", digitada no form de Apolices.jsx — ponto e' separador de milhar). So tem virgula
// no segundo caso, entao ela decide qual regra aplicar (sem isso "332.13" virava 33213 — o ponto
// decimal era removido como se fosse separador de milhar).
const parseValor = (v) => {
  if (v == null || v === '') return null
  if (typeof v === 'number') return v
  const s = String(v).trim()
  const n = s.includes(',')
    ? parseFloat(s.replace(/[R$\s.]/g, '').replace(',', '.'))
    : parseFloat(s.replace(/[R$\s]/g, ''))
  return Number.isNaN(n) ? null : n
}
const fmtValor = (v) => {
  const n = parseValor(v)
  return n != null ? `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '—'
}
const fmtDataHora = (iso) => iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''
const numeroApoliceExibido = (r) => r.emissao_numero_apolice || r.numero_apolice || '—'

// ===== Passo 1 do modal "+ Nova": busca a apólice por segurado ou nº, escolhe qual parcela =====
function NovaParcelaAtraso({ apolices, onCriado, onCancelar }) {
  const [busca, setBusca] = useState('')
  const [mostrarSugestoes, setMostrarSugestoes] = useState(false)
  const [apoliceEscolhida, setApoliceEscolhida] = useState(null)
  const [salvandoNumero, setSalvandoNumero] = useState(null)
  const [erro, setErro] = useState(null)
  const inputRef = useRef(null)
  const [posSugestoes, setPosSugestoes] = useState(null)

  // O modal tem overflow-y:auto — um dropdown position:absolute normal ficaria cortado nas
  // bordas dele. Por isso o dropdown e renderizado via portal em document.body, com a posicao
  // recalculada (fixed) a partir do input toda vez que ele reabre, no resize e ao rolar o modal.
  useEffect(() => {
    if (!mostrarSugestoes) return
    const atualizarPosicao = () => {
      const r = inputRef.current?.getBoundingClientRect()
      if (r) setPosSugestoes({ top: r.bottom, left: r.left, width: r.width })
    }
    atualizarPosicao()
    window.addEventListener('scroll', atualizarPosicao, true)
    window.addEventListener('resize', atualizarPosicao)
    return () => {
      window.removeEventListener('scroll', atualizarPosicao, true)
      window.removeEventListener('resize', atualizarPosicao)
    }
  }, [mostrarSugestoes])

  const q = busca.toLowerCase().trim()
  const sugestoes = q
    ? apolices.filter(a =>
        a.status === 'emitido' && Array.isArray(a.parcelas) && a.parcelas.length > 0 &&
        (a.cliente_nome?.toLowerCase().includes(q) || (a.numero_apolice || a.emissao_numero_apolice || '').toLowerCase().includes(q))
      ).slice(0, 8)
    : []

  const escolherApolice = (a) => {
    setApoliceEscolhida(a)
    setBusca(`${a.cliente_nome} — ${a.numero_apolice || a.emissao_numero_apolice || ''}`)
    setMostrarSugestoes(false)
  }

  // Um clique so: seleciona a parcela e ja registra o atraso com os dados dela (vencimento/valor/
  // forma de pagamento tal como estao na apolice), sem passo intermediario de revisao — so se
  // escolhe uma parcela por vez mesmo.
  const selecionarERegistrar = async (p, indice) => {
    const numero = p.numero || indice + 1
    setSalvandoNumero(numero)
    setErro(null)
    try {
      await api.post('/parcelas-atraso', {
        apolice_id: apoliceEscolhida.id,
        numero_parcela: numero,
        vencimento: p.vencimento || '',
        valor: p.valor,
        forma_pagamento: p.forma_pagamento,
      })
      onCriado()
    } catch (err) {
      setErro(err.response?.data?.erro || err.message)
      setSalvandoNumero(null)
    }
  }

  return (
    <div>
      <div className="form-group" style={{ position: 'relative' }}>
        <label className="form-label">Apólice (segurado ou nº)</label>
        <input ref={inputRef} className="form-input" value={busca} placeholder="Buscar por nome do segurado ou nº da apólice..."
          onChange={e => { setBusca(e.target.value); setApoliceEscolhida(null); setParcela(null); setMostrarSugestoes(true) }}
          onFocus={() => setMostrarSugestoes(true)}
          onBlur={() => setTimeout(() => setMostrarSugestoes(false), 150)} />
        {mostrarSugestoes && q && sugestoes.length > 0 && posSugestoes && createPortal(
          <div style={{ position: 'fixed', top: posSugestoes.top, left: posSugestoes.left, width: posSugestoes.width, zIndex: 300, background: '#fff', border: '1px solid #D9DDE3', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 260, overflowY: 'auto' }}>
            {sugestoes.map(a => (
              <button key={a.id} type="button" onMouseDown={() => escolherApolice(a)}
                style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', background: '#fff', padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #F1F3F5', fontSize: 13 }}>
                <strong>{a.cliente_nome}</strong> — {a.seguradora} {a.ramo} — nº {a.numero_apolice || a.emissao_numero_apolice || '—'}
              </button>
            ))}
          </div>,
          document.body
        )}
      </div>

      {apoliceEscolhida && (
        <div className="form-group">
          <label className="form-label">Qual parcela está em atraso?</label>
          <div className="table-wrap">
            <table className="tabela-parcelas-atraso">
              <thead><tr><th>Parc</th><th>Vencimento</th><th>Forma Pgto</th><th>Valor</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {apoliceEscolhida.parcelas.map((p, i) => {
                  const numero = p.numero || i + 1
                  return (
                    <tr key={i}>
                      <td>{numero}</td>
                      <td>{fmtData(p.vencimento)}</td>
                      <td>{p.forma_pagamento || '—'}</td>
                      <td>{fmtValor(p.valor)}</td>
                      <td>{p.status || 'Pendente'}</td>
                      <td>
                        <button type="button" className="btn btn-primary btn-sm" onClick={() => selecionarERegistrar(p, i)} disabled={salvandoNumero !== null}>
                          {salvandoNumero === numero ? 'Registrando...' : 'Registrar'}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {erro && <div className="alert alert-danger">{erro}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
        <button type="button" className="btn btn-secondary" onClick={onCancelar}>Cancelar</button>
      </div>
    </div>
  )
}

// ===== Acompanhamento: historico de comentarios com data/autor, mesmo padrao de Sinistros/Assistencias =====
function Acompanhamento({ registroId, onAlterado, rotulo }) {
  const [itens, setItens] = useState([])
  const [carregando, setCarregando] = useState(true)
  const [texto, setTexto] = useState('')
  const [enviando, setEnviando] = useState(false)

  const carregar = () => {
    setCarregando(true)
    api.get(`/parcelas-atraso/${registroId}/historico`)
      .then(r => setItens(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItens([]))
      .finally(() => setCarregando(false))
  }
  useEffect(() => { carregar() }, [registroId])

  const registrar = async () => {
    if (!texto.trim()) return
    setEnviando(true)
    try {
      await api.post(`/parcelas-atraso/${registroId}/historico`, { texto })
      setTexto('')
      carregar()
      onAlterado?.()
    } catch (err) {
      avisar('Erro ao salvar: ' + (err.response?.data?.erro || err.message))
    } finally {
      setEnviando(false)
    }
  }

  const remover = async (id) => {
    if (!(await confirmar('Remover esta anotação?'))) return
    await api.delete(`/parcelas-atraso/historico/${id}`)
    carregar()
    onAlterado?.()
  }

  return (
    <div>
      <div className="form-group">
        <label className="form-label" style={{ fontSize: 14 }}>{rotulo}</label>
        <textarea className="form-input" rows={4} style={{ resize: 'vertical' }}
          placeholder="Contato com o cliente, combinado de pagamento, retorno da seguradora..."
          value={texto} onChange={e => setTexto(e.target.value)} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
          <button type="button" className="btn btn-primary btn-sm" onClick={registrar} disabled={enviando || !texto.trim()}>
            {enviando ? 'Registrando...' : 'Registrar'}
          </button>
        </div>
      </div>
      {carregando ? (
        <div style={{ textAlign: 'center', color: '#aaa', padding: 20, fontSize: 13 }}>Carregando...</div>
      ) : itens.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#aaa', padding: 20, fontSize: 13 }}>Nenhum acompanhamento registrado ainda.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
          {itens.map(h => (
            <div key={h.id} style={{ background: '#FAFAFA', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#555' }}>{h.usuario_nome || 'Usuário'}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, color: '#999' }}>{fmtDataHora(h.created_at)}</span>
                  <button type="button" onClick={() => remover(h.id)} style={{ border: 'none', background: 'none', color: '#c33', cursor: 'pointer', fontSize: 12 }}>✕</button>
                </div>
              </div>
              <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{h.texto}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Conteudo (filtros + tabela + modais) compartilhado entre a tela normal (com Navbar/titulo,
// dentro do Layout) e a tela cheia aberta em outra aba (sem Navbar/titulo, pra exibir so a
// planilha — ver ParcelasAtrasoTelaCheia mais abaixo).
function ParcelasAtrasoConteudo() {
  const [registros, setRegistros] = useState([])
  const [apolices, setApolices] = useState([])
  const [carregando, setCarregando] = useState(true)
  const [modalNovo, setModalNovo] = useState(false)
  const [acompanhamentoId, setAcompanhamentoId] = useState(null)
  const [confirmarExcluirId, setConfirmarExcluirId] = useState(null)

  const [filtroStatus, setFiltroStatus] = useState('Pendente')
  // Mais de uma seguradora ao mesmo tempo (nenhuma marcada = todas) — pedido do usuario, 26/10
  const [filtroSeguradoras, setFiltroSeguradoras] = useState([])
  const [busca, setBusca] = useState('')

  const [sortCol, setSortCol] = useState('vencimento')
  const [sortDir, setSortDir] = useState('asc')
  const toggleSort = (key) => {
    if (sortCol === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(key); setSortDir('asc') }
  }

  const carregar = () => {
    setCarregando(true)
    return Promise.all([
      api.get('/parcelas-atraso'),
      api.get('/apolices'),
    ]).then(([registrosRes, apolicesRes]) => {
      setRegistros(Array.isArray(registrosRes.data) ? registrosRes.data : [])
      setApolices(Array.isArray(apolicesRes.data) ? apolicesRes.data : [])
    }).finally(() => setCarregando(false))
  }
  useEffect(() => { carregar() }, [])

  const alterarStatus = async (r, novoStatus) => {
    if (novoStatus === r.status) return
    await api.patch(`/parcelas-atraso/${r.id}/status`, { status: novoStatus })
    carregar()
  }

  const excluir = async (id) => {
    setConfirmarExcluirId(null)
    await api.delete(`/parcelas-atraso/${id}`)
    carregar()
  }

  const seguradoras = [...new Set(apolices.map(a => a.seguradora).filter(Boolean))].sort()

  const colunas = [
    { key: 'status',      label: 'Status',        get: r => r.status || 'Pendente' },
    { key: 'segurado',    label: 'Segurado',      get: r => r.cliente_nome || '' },
    { key: 'cpf',         label: 'CPF',           get: r => r.cliente_cpf || '' },
    { key: 'seguradora',  label: 'Seguradora',    get: r => r.seguradora || '' },
    { key: 'ramo',        label: 'Ramo',          get: r => r.ramo || '' },
    { key: 'apolice',     label: 'Apólice',       get: r => numeroApoliceExibido(r) },
    { key: 'parcela',     label: 'Parc',          get: r => r.numero_parcela, numeric: true },
    { key: 'vencimento',  label: 'Vencimento',    get: r => r.vencimento || '', date: true },
    { key: 'forma',       label: 'Forma Pgto',    get: r => r.forma_pagamento || '' },
    { key: 'valor',       label: 'Valor',         get: r => parseFloat(r.valor) || 0, numeric: true },
  ]

  const q = busca.toLowerCase().trim()
  const filtrados = registros.filter(r => {
    if (filtroStatus && r.status !== filtroStatus) return false
    if (filtroSeguradoras.length && !filtroSeguradoras.includes(r.seguradora)) return false
    if (q && !r.cliente_nome?.toLowerCase().includes(q)) return false
    return true
  })
  const ordenados = sortRows(filtrados, colunas, sortCol, sortDir)
  const paginacao = usePaginacao(ordenados)

  const registroAcompanhamento = registros.find(r => r.id === acompanhamentoId)
  const registroExcluir = registros.find(r => r.id === confirmarExcluirId)

  return (
    <>
        <div className="card">
          <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #eee' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ marginBottom: 0, minWidth: 260 }}>
                <label className="form-label">Buscar por segurado</label>
                <input className="form-input" value={busca} onChange={e => setBusca(e.target.value)} placeholder="Nome do segurado..." />
              </div>
              <div style={{ minWidth: 200 }}>
                <SelecaoMultipla label="Seguradora" opcoes={seguradoras.map(s => ({ valor: s, label: s }))} valores={filtroSeguradoras} onChange={setFiltroSeguradoras} />
              </div>
              <div className="form-group" style={{ marginBottom: 0, minWidth: 160 }}>
                <label className="form-label">Status</label>
                <select className="form-select" value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
                  <option value="Pendente">Pendente</option>
                  <option value="Quitado">Quitado</option>
                  <option value="">Todos</option>
                </select>
              </div>
              <div style={{ marginLeft: 'auto' }}>
                <button type="button" className="btn btn-primary" onClick={() => setModalNovo(true)}>+ Nova</button>
              </div>
            </div>
          </div>

          <div className="table-wrap">
            <table className="table-borda-destaque table-compacta">
              <thead>
                <tr>
                  {colunas.map(c => (
                    <SortableTh key={c.key} colKey={c.key} sortCol={sortCol} sortDir={sortDir} onSort={toggleSort}>{c.label}</SortableTh>
                  ))}
                  <th style={{ textAlign: 'center' }}>Acompanhamento</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {paginacao.itensPagina.map(r => {
                  return (
                    <tr key={r.id} style={{ opacity: r.status === 'Quitado' ? 0.6 : 1 }}>
                      <td style={{ textAlign: 'center' }}>
                        <select
                          className={`badge ${r.status === 'Pendente' ? 'badge-amber' : 'badge-green'}`}
                          style={{ cursor: 'pointer', border: 'none', fontFamily: 'inherit' }}
                          value={r.status || 'Pendente'}
                          onChange={e => alterarStatus(r, e.target.value)}>
                          <option value="Pendente">Pendente</option>
                          <option value="Quitado">Quitado</option>
                        </select>
                      </td>
                      <td style={{ fontWeight: 600 }}>{r.cliente_nome}</td>
                      <td style={{ textAlign: 'center' }}>{r.cliente_cpf || '—'}</td>
                      <td style={{ textAlign: 'center' }}>{r.seguradora || '—'}</td>
                      <td style={{ textAlign: 'center' }}>{r.ramo || '—'}</td>
                      <td style={{ textAlign: 'center' }}>{numeroApoliceExibido(r)}</td>
                      <td style={{ textAlign: 'center' }}>{r.numero_parcela}</td>
                      <td style={{ textAlign: 'center' }}>{fmtData(r.vencimento)}</td>
                      <td style={{ textAlign: 'center' }}>{r.forma_pagamento || '—'}</td>
                      <td style={{ textAlign: 'center' }}>{fmtValor(r.valor)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <div className="tooltip-balao-wrap">
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAcompanhamentoId(r.id)}>
                            {r.ultimo_acompanhamento_data ? fmtData(r.ultimo_acompanhamento_data) : 'Registrar'}
                          </button>
                          {r.ultimo_acompanhamento_texto && (
                            <div className="tooltip-balao">
                              {r.ultimo_acompanhamento_texto}
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button type="button" className="btn btn-danger btn-sm btn-icon" title="Excluir"
                          onClick={() => setConfirmarExcluirId(r.id)}><IconExcluir size={14} /></button>
                      </td>
                    </tr>
                  )
                })}
                {!carregando && ordenados.length === 0 && (
                  <tr><td colSpan={colunas.length + 2} style={{ textAlign: 'center', color: '#aaa', padding: 32 }}>Nenhuma parcela em atraso encontrada</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <Paginacao {...paginacao} rotulo="parcela" />
        </div>

      {modalNovo && (
        <div className="modal-overlay" onClick={() => setModalNovo(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 700 }}>Nova Parcela em Atraso</h2>
            <NovaParcelaAtraso apolices={apolices} onCancelar={() => setModalNovo(false)} onCriado={() => { setModalNovo(false); carregar() }} />
          </div>
        </div>
      )}

      {registroAcompanhamento && (
        <div className="modal-overlay" onClick={() => setAcompanhamentoId(null)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <Acompanhamento
              registroId={registroAcompanhamento.id}
              onAlterado={carregar}
              rotulo={`Acompanhamento — ${registroAcompanhamento.cliente_nome} — ${registroAcompanhamento.seguradora} — parcela ${registroAcompanhamento.numero_parcela}`}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setAcompanhamentoId(null)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {registroExcluir && (
        <div className="modal-overlay" onClick={() => setConfirmarExcluirId(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 700 }}>Excluir registro de atraso?</h2>
            <p style={{ fontSize: 13, color: '#555', margin: '0 0 20px' }}>
              {registroExcluir.cliente_nome} — {registroExcluir.seguradora} — parcela {registroExcluir.numero_parcela}.
              O histórico de acompanhamento junto com ele também será apagado. Essa ação não pode ser desfeita.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmarExcluirId(null)}>Cancelar</button>
              <button type="button" className="btn btn-danger btn-icon" title="Excluir" onClick={() => excluir(registroExcluir.id)}><IconExcluir size={16} /></button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default function ParcelasAtraso() {
  return (
    <>
      <div style={{ padding: '32px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>Parcelas em Atraso</h2>
          <div style={{ fontSize: 13, color: '#888', marginTop: 4, marginBottom: 20 }}>
            Controle das parcelas avisadas em atraso pela seguradora, com acompanhamento até a quitação.
          </div>
        </div>
        <button type="button" className="btn btn-secondary" onClick={() => window.open('/parcelas-atraso/tela-cheia', '_blank')}>
          Abrir em nova aba ↗
        </button>
      </div>

      <div className="page-content" style={{ paddingTop: 0 }}>
        <ParcelasAtrasoConteudo />
      </div>
    </>
  )
}

// Mesma tela, mas fora do Layout (sem Navbar) e sem o titulo — pensada pra ficar aberta numa
// aba/monitor a parte so com a planilha. Rota /parcelas-atraso/tela-cheia (ver App.jsx).
export function ParcelasAtrasoTelaCheia() {
  return (
    <div style={{ minHeight: '100vh', padding: 24 }}>
      <ParcelasAtrasoConteudo />
    </div>
  )
}
